/**
 * Exclusive Elite Prime family landing (mirrors `/elite-prime-landing`).
 *
 * Belongs here:
 * - Secondary already Prime → Active Prime dashboard
 * - Secondary free + family purchase → FamilyPurchaseBody
 * - Secondary Elite (not Prime) or blocked → SellBody (Upgrade / blocked CTA)
 * Does not belong here: tabbed Elite+Prime family landing (`/elite-plans-family-landing`).
 */
import '../elitePlansTheme.css'
import {
  ELITE_PLANS_ELITE_PRIME_TAB_ID,
  ElitePlansLandingProvider,
  useElitePlansLanding
} from '../ElitePlansLandingContext'
import { ElitePlansAnalyticsScreen } from '../constants/analyticsEvents'
import { ELITE_PRIME_FAMILY_LANDING_STATUS_QUERY_KEY } from '../constants/queryKeys'
import { ELITE_PRIME_DASHBOARD_FEATURES_ELITE_BUNDLE_LABEL } from '../constants/copy'
import {
  resolveIsFamilyPrimePurchaseForSecondary,
  resolveIsFamilySecondaryPrime
} from '../elitePlansMembership'
import { EliteBuyCtaExtrasProvider } from '../../../components/elite/EliteBuyCtaExtrasContext'
import { ElitePlansPageShell } from '../components/ElitePlansPageShell'
import { ElitePrimeDashboardHeader } from '../components/ElitePrimeDashboardHeader'
import { ElitePrimeDashboardHero } from '../components/ElitePrimeDashboardHero'
import { ElitePlansDashboardBelowFold } from '../components/ElitePlansDashboardBelowFold'
import { ElitePlansFamilyPurchaseBody } from '../components/ElitePlansFamilyPurchaseBody'
import { ElitePlansSellLandingChrome } from '../components/ElitePlansSellLandingChrome'
import { ElitePlansSellBody } from '../components/ElitePlansSellBody'
import { ElitePlansHeroStatusSkeleton } from '../components/hero/ElitePlansHeroStatusSkeleton'
import { useTrackElitePlansScreenLoadWhenReady } from '../hooks/useTrackElitePlansScreenLoadWhenReady'

export default function ElitePrimeFamilyLandingPage() {
  return (
    <ElitePlansLandingProvider
      analyticsScreen={ElitePlansAnalyticsScreen.PrimeFamilyLanding}
      lockedPlanId={ELITE_PLANS_ELITE_PRIME_TAB_ID}
      statusQueryKeyRoot={ELITE_PRIME_FAMILY_LANDING_STATUS_QUERY_KEY}
    >
      <ElitePrimeFamilyLandingPageContent />
    </ElitePlansLandingProvider>
  )
}

function ElitePrimeFamilyLandingPageContent() {
  const { status, isStatusLoading } = useElitePlansLanding()
  useTrackElitePlansScreenLoadWhenReady(!isStatusLoading)

  // Wait for status — do not flash SellBody before FamilyPurchase / dashboard resolves
  if (isStatusLoading) {
    return (
      <ElitePlansPageShell isContentScrollOwner bottomInsetPx={24}>
        <ElitePrimeDashboardHeader />
        <div className='px-5'>
          <ElitePlansHeroStatusSkeleton showTenure showPlusBanner />
        </div>
      </ElitePlansPageShell>
    )
  }

  // Secondary already has Prime → Active Prime dashboard (not purchase)
  if (resolveIsFamilySecondaryPrime(status)) {
    return (
      <ElitePlansPageShell>
        <ElitePrimeDashboardHeader />
        <ElitePrimeDashboardHero />
        <ElitePlansDashboardBelowFold
          isContentLoading={false}
          eliteBundleLabel={ELITE_PRIME_DASHBOARD_FEATURES_ELITE_BUNDLE_LABEL}
        />
      </ElitePlansPageShell>
    )
  }

  // Free secondary → family Continue (Elite secondary uses SellBody below)
  if (resolveIsFamilyPrimePurchaseForSecondary(status)) {
    return (
      <EliteBuyCtaExtrasProvider status={status}>
        <ElitePlansPageShell isContentScrollOwner bottomInsetPx={24}>
          <ElitePrimeDashboardHeader />
          <ElitePlansFamilyPurchaseBody />
        </ElitePlansPageShell>
      </EliteBuyCtaExtrasProvider>
    )
  }

  return (
    <ElitePlansSellLandingChrome>
      <ElitePrimeDashboardHeader />
      <ElitePlansSellBody />
    </ElitePlansSellLandingChrome>
  )
}
